import React, { useEffect, useState, useRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Tooltip,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  ClickAwayListener,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel,
  Select,
  Drawer,
  MenuItem,
  CircularProgress,
  Typography,
} from '@mui/material';

import {
  FilterList as FilterIcon,
  Add as AddIcon,
  GetApp as ExportIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';

import { PageHeader } from '@/components/ui';
import type { PageHeaderAction } from '@/components/ui/PageHeader';

import { useCommonBreadcrumbs } from '@/utils/breadcrumbs';
import { getBreadcrumbConfig } from '@/config/breadcrumbs';
import { formatCurrency } from '@/utils/formatters';
import { format as formatMomentDate } from '@/utils/time';

import { superadminInvoicesService } from '../../../../services/invoicesService';
import { toast } from 'react-hot-toast';
import type { SuperadminInvoice } from '@/superadmin/pages/invoice/types/superadminInvoice';
import { FilterPanel } from '@/superadmin/pages/invoice/components/InvoiceFilters/FilterPanel';
import type { SuperadminInvoiceFilters } from '@/superadmin/pages/invoice/types/superadminInvoice';
import { useSuperadminAuth } from '@/superadmin/contexts/SuperadminAuthContext';

/* =========================================================
   Component
========================================================= */

const SuperadminInvoicesPage: React.FC = () => {
  const [invoices, setInvoices] = useState<SuperadminInvoice[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<SuperadminInvoice | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalCount, setTotalCount] = useState(0);

  const [isGenerateDrawerOpen, setIsGenerateDrawerOpen] = useState(false);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const [filters, setFilters] = useState<InvoiceFilters>({
    searchTerm: '',
    statusFilter: '',
    merchants: [], // ✅ ADD
    dateRange: { startDate: '', endDate: '' },
  });
  const [formData, setFormData] = useState({
    merchant_uuid: '',
    date_from: '',
    date_to: '',
  });

  const [searchKey, setSearchKey] = useState<'invoice_number' | 'merchant_uuid'>('invoice_number');
  const [searchValue, setSearchValue] = useState('');

  const [merchants, setMerchants] = useState<any[]>([]);

  const [appliedFilters, setAppliedFilters] = useState<{
    start_date?: string;
    end_date?: string;
    invoice_id?: string;
    invoice_status?: string;
    merchant_uuid?: string;
  }>({});

  const { user } = useSuperadminAuth();

  if (!user) return <div>No Access</div>;

  const hasPermission = (permission: string) => {
    if (user.is_superuser) return true; // superuser has everything
    return user.permission_list?.includes(permission);
  };

  useEffect(() => {
    fetchMerchants();
  }, []);

  // console.log('users......', user);

  /* =========================================================
     Fetch Invoices
  ========================================================= */

  //  const fetchInvoices = async () => {
  //   setIsLoading(true);

  //   try {
  //     const response = await superadminInvoicesService.list({
  //       invoice_id: appliedFilters.invoice_id,
  //       date_from: appliedFilters.date_from,
  //       date_to: appliedFilters.date_to,
  //       invoice_status: appliedFilters.invoice_status,
  //       page: currentPage,
  //       page_size: pageSize,
  //     });

  //     console.log('API RESPONSE:', response);

  //     // ✅ CORRECT MAPPING
  //     setInvoices(response?.results?.data ?? []);
  //     setTotalCount(response?.count ?? 0);
  //   } catch (error) {
  //     console.error(error);
  //     toast.error('Failed to load invoices');
  //     setInvoices([]);
  //     setTotalCount(0);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const fetchInvoices = async () => {
    setIsLoading(true);

    try {
      const response = await superadminInvoicesService.list({
        invoice_id: appliedFilters.invoice_id,
        invoice_status: appliedFilters.invoice_status,
        merchant_uuid: appliedFilters.merchant_uuid,
        date_from: appliedFilters.start_date,
        date_to: appliedFilters.end_date,
        page: currentPage,
        page_size: pageSize,
      });

      setInvoices(response?.results?.data ?? []);
      setTotalCount(response?.count ?? 0);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load invoices');
      setInvoices([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterPanelOpen = () => {
    setIsFilterPanelOpen(true);
  };

  const handleFilterPanelClose = () => {
    setIsFilterPanelOpen(false);
  };

  useEffect(() => {
    if (isGenerateDrawerOpen) {
      fetchMerchants();
    }
  }, [isGenerateDrawerOpen]);

  const fetchMerchants = async () => {
    try {
      const res = await superadminInvoicesService.listMerchants();

      // console.log('Merchant API response:', res); // 👈 keep once to verify

      // ✅ interceptor already returned data
      setMerchants(res.results || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch merchants');
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [currentPage, pageSize, appliedFilters]);

  /* =========================================================
     Handlers
  ========================================================= */

  const handleFiltersChange = (newFilters: SuperadminInvoiceFilters) => {
    console.log('InvoicesPage - Received filters:', newFilters);
    setFilters(newFilters);
  };

  // const handleApplyFilters = () => {
  //   setIsLoading(true);

  //   try {
  //     const applied: any = {};

  //     // Date range
  //     if (filters.dateRange?.startDate && filters.dateRange?.endDate) {
  //       const formatDate = (dateStr: string) => new Date(dateStr).toISOString().split('T')[0];

  //       applied.start_date = formatDate(filters.dateRange.startDate);
  //       applied.end_date = formatDate(filters.dateRange.endDate);
  //     }

  //     // Status (SINGLE value as backend expects)
  //     if (filters.statusFilter) {
  //       applied.invoice_status = filters.statusFilter;
  //     }

  //     // Merchant (SINGLE UUID)
  //     if (filters.merchants?.length === 1) {
  //       applied.merchant_uuid = filters.merchants[0].uuid;
  //     }

  //     // Apply filters
  //     setAppliedFilters(applied);
  //     setCurrentPage(1);
  //   } catch (error) {
  //     console.error('Error applying filters:', error);
  //     toast.error('Failed to apply filters');
  //   } finally {
  //     setIsLoading(false);
  //     setIsFilterPanelOpen(false);
  //   }
  // };

  const handleApplyFilters = () => {
    const applied: any = {};

    if (filters.dateRange?.startDate && filters.dateRange?.endDate) {
      applied.start_date = filters.dateRange.startDate.split('T')[0];
      applied.end_date = filters.dateRange.endDate.split('T')[0];
    }

    if (filters.statusFilter) {
      applied.invoice_status = filters.statusFilter;
    }

    if (filters.merchants?.length === 1) {
      applied.merchant_uuid = filters.merchants[0].uuid;
    }

    setAppliedFilters(applied);
    setCurrentPage(1);
    setIsFilterPanelOpen(false);
  };

  const handleSearch = () => {
    if (!searchValue.trim()) return;

    setAppliedFilters(prev => ({
      ...prev,
      invoice_id: searchValue.trim(),
    }));

    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setAppliedFilters({});
    setFilters({
      searchTerm: '',
      statusFilter: '',
      merchants: [],
      dateRange: { startDate: '', endDate: '' },
    });
    setSearchValue('');
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    handleClearFilters();
    fetchInvoices();
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setCurrentPage(newPage + 1);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const handleGenerateInvoice = async () => {
    // Date validation
    if (new Date(formData.date_to) < new Date(formData.date_from)) {
      toast.error('End date must be greater than or equal to start date');
      return;
    }

    try {
      const response = await superadminInvoicesService.generate(formData);

      // 🔴 IMPORTANT: handle business error
      if (response?.success === false) {
        toast.error(response?.message || 'Failed to generate invoice');
        return;
      }

      toast.success('Invoice generated successfully');
      setIsGenerateDrawerOpen(false);
      fetchInvoices();
    } catch (err: any) {
      console.error(err);

      // 🔴 HTTP / Network errors
      toast.error(err?.response?.data?.message || err?.message || 'Failed to generate invoice');
    }
  };

  const handleViewInvoice = (invoice: SuperadminInvoice) => {
    setSelectedInvoice(invoice);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedInvoice(null);
  };

  const handleDownloadInvoice = async (invoice_id: string) => {
    try {
      const response = await superadminInvoicesService.download(invoice_id);

      // If backend sends success false even with 200 status
      if (response?.success === false) {
        toast.error(response.message || 'Invoice download failed');
        return;
      }

      if (!response?.download_link) {
        toast.error('Download link not available');
        return;
      }

      window.open(response.download_link, '_blank');
    } catch (error: any) {
      console.error('Download invoice error:', error);

      // Axios / fetch error handling
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Failed to download invoice';

      toast.error(errorMessage);
    }
  };

  const handleResendInvoice = async (invoice_id: string) => {
    try {
      await superadminInvoicesService.resend({ invoice_id });
      toast.success('Invoice resent successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to resend invoice');
    }
  };

  /* =========================================================
     Breadcrumbs & Header
  ========================================================= */

  const breadcrumbConfig = getBreadcrumbConfig('superadmin');
  const finalBreadcrumbs = useCommonBreadcrumbs({
    hideBreadcrumbs: breadcrumbConfig.hideBreadcrumbs,
  });

  const headerActions: PageHeaderAction[] = [
    hasPermission('ADMIN_INVOICE_GENERATE') && {
      label: 'Generate Invoice',
      icon: <AddIcon />,
      onClick: () => setIsGenerateDrawerOpen(true),
      variant: 'contained',
      size: 'small',
    },
  ].filter(Boolean) as PageHeaderAction[];

  /* =========================================================
     Render
  ========================================================= */

  return (
    <Box>
      <PageHeader
        title="Invoices"
        subtitle={`${totalCount} invoice${totalCount === 1 ? '' : 's'}`}
        breadcrumbs={finalBreadcrumbs}
        actions={headerActions}
        showDivider={false}
      />
      {/* Search Bar */}
      <Box display="flex" gap={2} mb={2}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select value={searchKey} onChange={e => setSearchKey(e.target.value as any)}>
            <MenuItem value="invoice_number">Invoice Number</MenuItem>
            <MenuItem value="invoice_id"> Invoice ID</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder="Search..."
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <Button onClick={handleClearFilters} size="small">
          Clear
        </Button>
        <IconButton size="small" onClick={handleRefresh}>
          <RefreshIcon />
        </IconButton>
      </Box>
      <Box sx={{ mb: 4 }}>
        <Button
          ref={filterButtonRef}
          variant="outlined"
          size="small"
          onClick={handleFilterPanelOpen}
          endIcon={<FilterIcon />}
          startIcon={<AddIcon />}
          sx={{
            minWidth: { xs: '100%', sm: 'auto' },
            textTransform: 'none',
          }}
        >
          Add Filters
        </Button>

        {isFilterPanelOpen && (
          <ClickAwayListener onClickAway={handleFilterPanelClose}>
            <Box>
              <FilterPanel
                open={isFilterPanelOpen}
                anchorEl={filterButtonRef.current}
                onClose={handleFilterPanelClose}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onApplyFilters={handleApplyFilters}
                onClearFilters={handleClearFilters}
                merchants={merchants} // ✅ ADD
              />
            </Box>
          </ClickAwayListener>
        )}
      </Box>
      {/* Table */}
      {hasPermission('ADMIN_INVOICE_LIST_VIEW') ? (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice Id</TableCell>
                  <TableCell>Invoice No</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Initiated</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell>Transactions</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      No invoices found
                    </TableCell>
                  </TableRow>
                ) : (
                  Array.isArray(invoices) &&
                  invoices.map(invoice => (
                    <TableRow key={invoice.invoice_id} hover>
                      <TableCell
                        sx={{
                          fontFamily: 'monospace',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {invoice.invoice_id}
                        <Tooltip title="Copy">
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(invoice.invoice_id);
                              toast.success('Copied!');
                            }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>

                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {invoice.invoice_number}
                      </TableCell>

                      <TableCell>{formatCurrency(Number(invoice.invoice_amount))}</TableCell>

                      <TableCell>
                        <Chip label={invoice.invoice_status} size="small" />
                      </TableCell>

                      <TableCell>
                        {formatMomentDate(invoice.invoice_initiated_date, 'DD MMM YYYY')}
                      </TableCell>

                      <TableCell>
                        {invoice.invoice_expiry_date
                          ? formatMomentDate(invoice.invoice_expiry_date, 'DD MMM YYYY')
                          : '—'}
                      </TableCell>

                      <TableCell>{invoice.transaction_invoice?.length ?? 0}</TableCell>

                      <TableCell align="center">
                        <Tooltip title="View Invoice">
                          <IconButton onClick={() => handleViewInvoice(invoice)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Download Invoice">
                          <IconButton onClick={() => handleDownloadInvoice(invoice.invoice_id)}>
                            <ExportIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleResendInvoice(invoice.invoice_id)}
                        >
                          Resend
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={currentPage - 1}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[10, 15, 25, 50]}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handlePageSizeChange}
          />
        </Paper>
      ) : (
        <Typography variant="body1">You do not have permission to view this page.</Typography>
      )}

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 520 },
            p: 3,
            pt: 6,
          },
        }}
      >
        {selectedInvoice && (
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Close Button */}
            <IconButton
              onClick={handleCloseDrawer}
              sx={{ position: 'absolute', top: 12, right: 12 }}
            >
              <CloseIcon />
            </IconButton>

            {/* Header */}
            <Typography variant="h6">Invoice {selectedInvoice.invoice_number}</Typography>
            {selectedInvoice.invoice_note && (
              <Typography variant="body2" color="text.secondary">
                {selectedInvoice.invoice_note}
              </Typography>
            )}

            {/* Basic Info */}
            {/* <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography>
                <strong>Status:</strong> {selectedInvoice.invoice_status}
              </Typography>
              <Typography>
                <strong>Amount:</strong> ₹{selectedInvoice.invoice_amount}
              </Typography>
              <Typography>
                <strong>Period:</strong> {selectedInvoice.metadata.date_from} →{' '}
                {selectedInvoice.metadata.date_to}
              </Typography>
              <Typography>
                <strong>Initiated:</strong> {selectedInvoice.invoice_initiated_date}
              </Typography>
              <Typography>
                <strong>Settled:</strong> {selectedInvoice.invoice_settled_date ?? '—'}
              </Typography>
            </Paper> */}

            {/* Payment Info */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography fontWeight={600} mb={1}>
                Payment Details
              </Typography>
              <Typography>
                <strong>Mode:</strong> {selectedInvoice.mode_of_payment ?? '—'}
              </Typography>
              <Typography>
                <strong>Bank:</strong> {selectedInvoice.bank_name ?? '—'}
              </Typography>
              <Typography>
                <strong>IFSC:</strong> {selectedInvoice.ifsc ?? '—'}
              </Typography>
              <Typography>
                <strong>UTR:</strong> {selectedInvoice.utr ?? '—'}
              </Typography>
              <Typography>
                <strong>Payer:</strong> {selectedInvoice.payer_name ?? '—'}
              </Typography>
            </Paper>

            {/* Line Items */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography fontWeight={600} mb={1}>
                Line Items
              </Typography>
              <Typography>
                <strong>Total Transactions:</strong> ₹
                {selectedInvoice.metadata.line_items.total_transaction_amount}
              </Typography>
              <Typography>
                <strong>Total Commission:</strong> ₹
                {selectedInvoice.metadata.line_items.total_commission}
              </Typography>
              <Typography>
                <strong>Generated Via:</strong> {selectedInvoice.metadata.generated_via}
              </Typography>
              <Typography>
                <strong>Resend Count:</strong> {selectedInvoice.metadata.resend_count}
              </Typography>
            </Paper>

            {/* Transactions */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography fontWeight={600} mb={1}>
                Transactions
              </Typography>
              {selectedInvoice.transaction_invoice.map(tx => (
                <Box key={tx.transaction_id} display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography>TX #{tx.transaction_id}</Typography>
                  <Typography>₹{tx.transaction_amount}</Typography>
                </Box>
              ))}
            </Paper>
          </Box>
        )}
      </Drawer>
      <Drawer
        anchor="right"
        open={isGenerateDrawerOpen}
        onClose={() => setIsGenerateDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 420 },
            p: 3,
            pt: 6,
          },
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={() => setIsGenerateDrawerOpen(false)}
          sx={{ position: 'absolute', top: 12, right: 12 }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h6" mb={2}>
          Generate Invoice
        </Typography>

        <Box display="flex" flexDirection="column" gap={2}>
          {/* Merchant Select */}
          <FormControl fullWidth>
            <InputLabel id="merchant-label">Merchant</InputLabel>
            <Select
              labelId="merchant-label"
              label="Merchant"
              value={formData.merchant_uuid}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  merchant_uuid: e.target.value,
                }))
              }
            >
              {merchants.length === 0 && <MenuItem disabled>No merchants found</MenuItem>}

              {merchants.map(merchant => (
                <MenuItem key={merchant.uuid} value={merchant.uuid}>
                  {merchant.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date From */}
          <TextField
            label="Date From"
            type="date"
            value={formData.date_from}
            onChange={e =>
              setFormData(prev => ({
                ...prev,
                date_from: e.target.value,
              }))
            }
            InputLabelProps={{ shrink: true }}
          />

          {/* Date To */}
          <TextField
            label="Date To"
            type="date"
            value={formData.date_to}
            onChange={e =>
              setFormData(prev => ({
                ...prev,
                date_to: e.target.value,
              }))
            }
            InputLabelProps={{ shrink: true }}
            error={Boolean(
              formData.date_from &&
                formData.date_to &&
                new Date(formData.date_to) < new Date(formData.date_from)
            )}
            helperText={
              formData.date_from &&
              formData.date_to &&
              new Date(formData.date_to) < new Date(formData.date_from)
                ? 'End date must be greater than or equal to start date'
                : ''
            }
          />

          {/* Action Buttons */}
          <Box display="flex" gap={2} mt={2}>
            <Button fullWidth variant="outlined" onClick={() => setIsGenerateDrawerOpen(false)}>
              Cancel
            </Button>

            <Button
              fullWidth
              variant="contained"
              disabled={
                !formData.merchant_uuid ||
                !formData.date_from ||
                !formData.date_to ||
                new Date(formData.date_to) < new Date(formData.date_from)
              }
              onClick={handleGenerateInvoice}
            >
              Generate
            </Button>

            {/* <Button
              fullWidth
              variant="contained"
              disabled={
                !formData.merchant_uuid ||
                !formData.date_from ||
                !formData.date_to ||
                new Date(formData.date_to) < new Date(formData.date_from)
              }
              onClick={async () => {
                // Extra safety check
                if (new Date(formData.date_to) < new Date(formData.date_from)) {
                  toast.error('End date must be greater than or equal to start date');
                  return;
                }

                try {
                  await superadminInvoicesService.generate(formData);
                  toast.success('Invoice generated successfully');
                  setIsGenerateDrawerOpen(false);
                  fetchInvoices();
                } catch (err: any) {
                  console.error(err);

                  toast.error(err?.response?.data?.message || 'Failed to generate invoice');
                }
              }}
            >
              Generate
            </Button> */}
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default SuperadminInvoicesPage;
